import { cn } from "@breakout/ui/utils"
import { Clock } from "lucide-react"
import { useCountdown } from "../../hooks/common/useCountdown"

type CountdownState = "active" | "opens-soon" | "ended"

interface CountdownBadgeProps {
	/** Target timestamp (close time) in milliseconds */
	targetMs: number | null
	/** Optional open timestamp in milliseconds - shows "Opens in X" if in future */
	openTimeMs?: number | null
	/** Badge size variant */
	size?: "sm" | "md"
	/** Additional className */
	className?: string
}

const sizeStyles = {
	sm: {
		badge: "gap-1 px-1.5 py-0.5 text-xs",
		icon: "size-2.5",
	},
	md: {
		badge: "gap-2 px-3 py-1.5 text-sm shadow-sm",
		icon: "size-4",
	},
} as const

const stateStyles: Record<CountdownState, string> = {
	active: "animate-pulse-slow border-breakout-brand/30 bg-breakout-brand/10 text-breakout-brand",
	"opens-soon": "border-amber-500/30 bg-amber-500/10 text-amber-500",
	ended: "border-border-subtle bg-bg-tertiary text-text-muted",
}

/**
 * Unified countdown badge component for displaying time remaining
 * Handles three states:
 * - "opens-soon": When openTimeMs is in the future
 * - "active": Counting down to targetMs
 * - "ended": When targetMs has passed
 */
export function CountdownBadge({
	targetMs,
	openTimeMs,
	size = "md",
	className,
}: CountdownBadgeProps) {
	// Countdown to close time
	const { timeRemaining: closeTimeRemaining, isEnded } = useCountdown(targetMs)

	// Countdown to open time (if provided)
	const { timeRemaining: openTimeRemaining, isEnded: isOpen } = useCountdown(openTimeMs ?? null)

	// Determine current state
	const isOpensSoon = openTimeMs != null && !isOpen
	const state: CountdownState = isOpensSoon ? "opens-soon" : isEnded ? "ended" : "active"

	// Determine display text
	const displayText = isOpensSoon ? `Opens in ${openTimeRemaining}` : closeTimeRemaining

	const styles = sizeStyles[size]

	return (
		<span
			className={cn(
				"flex shrink-0 items-center rounded-full border font-medium transition-all",
				styles.badge,
				stateStyles[state],
				className,
			)}
		>
			<Clock aria-hidden="true" className={styles.icon} />
			<span>{displayText}</span>
		</span>
	)
}
